import type { SyntheticEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSpace } from '../../../../hooks/useSpace';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { usePermissions } from '../../../../hooks/usePermissions';

export type SettingsSection =
  | 'copropriete'
  | 'document-types'
  | 'google-drive'
  | 'intelligence-artificielle'
  | 'indexation';

export const VALID_SECTIONS: SettingsSection[] = [
  'copropriete',
  'document-types',
  'google-drive',
  'intelligence-artificielle',
  'indexation',
];

// Former section slugs, still reachable from old links.
const LEGACY_COPROPRIETE_SECTIONS = ['general', 'members'];

function isSettingsSection(value: string): value is SettingsSection {
  return VALID_SECTIONS.some((section) => section === value);
}

export function resolveSettingsSection(sectionParam?: string | null): SettingsSection {
  if (!sectionParam || LEGACY_COPROPRIETE_SECTIONS.includes(sectionParam)) {
    return 'copropriete';
  }
  return isSettingsSection(sectionParam) ? sectionParam : 'copropriete';
}

export default function useSpaceSettingsPage() {
  const { currentSpace } = useSpace();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const params = useParams<{ section?: string }>();

  const activeSection = resolveSettingsSection(params.section);

  function handleSectionChange(_event: SyntheticEvent, section: SettingsSection) {
    navigate(spacePath(`/settings/${section}`));
  }

  return {
    currentSpace,
    isAdmin,
    activeSection,
    handleSectionChange,
  };
}
